import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Semantic colour. `neutral` is the flat gray used for anything without a status. */
export type BzTagTone = 'neutral' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger';

/** Text on a custom background: white on dark colours, the body text colour on light ones. */
const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = 'var(--bz-color-text)';
/** Relative luminance of --bz-color-text (#212121), the dark option's real value. */
const DARK_TEXT_LUMINANCE = 0.0152;

/**
 * Relative luminance (WCAG 2.x) of a `#rgb`, `#rrggbb` or `rgb()/rgba()`
 * colour, or null for anything else — a named colour or a `var()` cannot be
 * resolved here without the DOM.
 */
export function bzColorLuminance(color: string): number | null {
  const value = color.trim().toLowerCase();
  let rgb: number[] | null = null;

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(value);
  if (hex) {
    const digits = hex[1].length === 3 ? [...hex[1]].map((d) => d + d).join('') : hex[1];
    rgb = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
  }
  const fn = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(value);
  if (fn) {
    rgb = [fn[1], fn[2], fn[3]].map(Number);
  }
  if (!rgb) return null;

  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * A small, tinted label pill. Content-projected, so it renders plain text or
 * `bz-icon` + text alike.
 *
 * Colour comes from `tone`, or from `color` when the colour is data rather
 * than meaning — a tag or a skill whose colour a user picked. With `color`,
 * the text colour is chosen for contrast (whichever of white and the body text
 * colour reads better on it), unless `textColor` says otherwise.
 *
 * Not a drop-in replacement for the console's `app-bringoz-tag`: that
 * component also looked up a colour by name from a skills list injected via
 * `splitService`. That lookup is application logic, not a design-system
 * concern — a consumer resolves the colour itself and passes it in, the same
 * way every other bz-* component takes data through inputs.
 */
@Component({
  selector: 'bz-tag',
  templateUrl: './bz-tag.html',
  styleUrl: './bz-tag.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzTag {
  readonly tone = input<BzTagTone>('neutral');
  /** Any CSS colour for the background. Overrides `tone`. */
  readonly color = input<string | null>(null);
  /** Text colour. With a custom `color` and no `textColor`, it is chosen for contrast. */
  readonly textColor = input<string | null>(null);

  protected readonly tagClass = computed(() => `bz-tag bz-tag--${this.tone()}`);

  protected readonly foreground = computed(() => {
    if (this.textColor()) return this.textColor();
    const color = this.color();
    if (!color) return null;
    const luminance = bzColorLuminance(color);
    // Unknown luminance (a named colour, a var()): dark text, which is right
    // for the pale tints tags are usually given.
    if (luminance === null) return DARK_TEXT;
    return contrast(luminance, 1) >= contrast(luminance, DARK_TEXT_LUMINANCE) ? LIGHT_TEXT : DARK_TEXT;
  });
}
